import React, { useCallback, useEffect, useState } from 'react';
import { Scale } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const Row = ({ r, onDone }) => {
  const [paid, setPaid] = useState(r.suggested_paid_days);
  const [busy, setBusy] = useState(false);
  const decide = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/leaves/${r.leave_id}/pay-decision`, { paid_days: Number(value) });
      toast({ title: 'Payroll updated', description: `${r.employee_name}: ${data.paid_days} paid / ${data.unpaid_days} unpaid day(s).` });
      onDone();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2" data-testid={`pay-decision-row-${r.leave_id}`}>
      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${r.reason === 'emergency' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800'}`}>
        {r.reason === 'emergency' ? 'Emergency leave' : 'Not eligible for paid'}
      </span>
      <div className="flex-1 min-w-[240px]">
        <p className="text-sm font-semibold text-gray-900">{r.employee_name} <span className="text-xs font-normal text-gray-500">{r.employee_code} · {r.department}</span></p>
        <p className="text-xs text-gray-600">
          <span className="capitalize">{r.leave_type}</span> · {fmtDate(r.start_date)} – {fmtDate(r.end_date)} · <b>{r.days} day(s)</b>
          {r.approved_by ? ` · approved by ${r.approved_by}` : ''}
          {r.reason === 'not_eligible' ? ` · balance covers ${r.suggested_paid_days} day(s)` : ''}
          {r.leave_reason ? <span className="italic"> · “{r.leave_reason}”</span> : null}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => decide(r.days)} data-testid={`pay-decision-paid-${r.leave_id}`}>All paid</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" disabled={busy} onClick={() => decide(0)} data-testid={`pay-decision-unpaid-${r.leave_id}`}>All unpaid</Button>
        <span className="text-xs text-gray-400">or</span>
        <Input type="number" min={0} max={r.days} step={0.5} value={paid} onChange={(e) => setPaid(e.target.value)} className="h-7 w-16 text-xs" data-testid={`pay-decision-days-${r.leave_id}`} />
        <span className="text-xs text-gray-500">paid</span>
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy || paid === '' || Number(paid) < 0 || Number(paid) > r.days} onClick={() => decide(paid)} data-testid={`pay-decision-split-${r.leave_id}`}>Save split</Button>
      </div>
    </div>
  );
};

export default function PayDecisionCard() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/api/leaves/pay-decisions/pending').then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);
  if (!rows.length) return null;
  return (
    <Card className="border-2 border-amber-400 leave-alert-blink" data-testid="pay-decision-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-amber-600" />
          <h3 className="font-semibold text-gray-900">Paid or unpaid? HR decision needed</h3>
          <span className="text-[11px] font-bold text-white bg-amber-600 rounded-full px-2 py-0.5">{rows.length}</span>
          <span className="ml-auto text-xs text-gray-500">Counted as paid in payroll until you decide</span>
        </div>
        {rows.map((r) => <Row key={r.leave_id} r={r} onDone={load} />)}
      </CardContent>
    </Card>
  );
}
